import { seedFrom, seededStream } from '@shared/rng';

/** Opt-in only. Never patch ambient randomness or browser clocks in production. */
const workerSeed = typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope
  ? /^nyc-det:([0-9]+):/.exec((self as unknown as { name: string }).name) : null;
const query = new URLSearchParams(workerSeed ? `det=1&seed=${workerSeed[1]}` : globalThis.location?.search ?? '');
export const DETERMINISTIC = query.get('det') === '1';
export const DET_SEED = seedFrom(query.get('seed') ?? '424242');
export const FIXED_DT = 1 / 60;
export const FIXED_EPOCH = 1759406400000;
let frame = 0;

export const nowMs = (): number => DETERMINISTIC ? frame * 1000 / 60 : performance.now();
export const wallTime = (): number => DETERMINISTIC ? FIXED_EPOCH + frame * 1000 / 60 : Date.now();

/** Independent streams prevent async audio work from changing combat/animation RNG. */
export function randomStream(name: string): () => number {
  return DETERMINISTIC ? seededStream(DET_SEED, name) : () => Math.random();
}

export function deterministicWorkerName(name: string): string {
  return DETERMINISTIC ? `nyc-det:${DET_SEED}:${name}` : name;
}

/** Stable entity iteration, with the original iterator and zero sorting in production. */
export function orderedEntries<K extends string | number, V>(map: Map<K, V>): Iterable<[K, V]> {
  return DETERMINISTIC ? [...map].sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0) : map;
}

let serial = 0;
const timers = new Map<number, { at: number; fn: () => void; interval?: number }>();
export function gameTimeout(fn: () => void, delay: number): number {
  if (!DETERMINISTIC) return globalThis.setTimeout(fn, delay) as unknown as number;
  const id = ++serial;
  timers.set(id, { at: nowMs() + Math.max(0, delay), fn });
  return id;
}
export function clearGameTimeout(id: number | undefined): void {
  if (!DETERMINISTIC) globalThis.clearTimeout(id);
  else if (id !== undefined) timers.delete(id);
}
export function gameInterval(fn: () => void, delay: number): number {
  if (!DETERMINISTIC) return globalThis.setInterval(fn, delay) as unknown as number;
  const id = ++serial, interval = Math.max(FIXED_DT * 1000, delay);
  timers.set(id, { at: nowMs() + interval, fn, interval });
  return id;
}
export function clearGameInterval(id: number | undefined): void {
  if (!DETERMINISTIC) globalThis.clearInterval(id);
  else if (id !== undefined) timers.delete(id);
}
export function beginDeterministicFrame(value: number): void {
  if (!DETERMINISTIC) return;
  frame = value;
  for (const [id, timer] of [...timers].sort((a, b) => a[1].at - b[1].at || a[0] - b[0])) {
    if (timer.at > nowMs()) break;
    if (!timers.has(id)) continue;
    if (timer.interval) timer.at += timer.interval;
    else timers.delete(id);
    timer.fn();
  }
}

// Native asynchronous DSP rendering must finish before advancing simulation time.
const pending = new Set<Promise<unknown>>();
export function deterministicJob<T>(job: Promise<T>): Promise<T> {
  if (!DETERMINISTIC) return job;
  pending.add(job);
  void job.then(() => pending.delete(job), () => pending.delete(job));
  return job;
}
export async function settleDeterministicJobs(): Promise<void> {
  while (pending.size) await Promise.all([...pending]);
}

/** Keep native worker latency out of the next simulation frame. The constructor
 * remains at each call site so Vite can bundle its static worker URL normally.
 */
export function trackDeterministicWorker(worker: Worker): Worker {
  if (!DETERMINISTIC) return worker;
  const requests = new Map<unknown, () => void>();
  const send = worker.postMessage.bind(worker);
  worker.postMessage = ((message: { id?: unknown; type?: string }, transfer?: Transferable[]) => {
    if (message?.type === 'cancel') { requests.get(message.id)?.(); requests.delete(message.id); }
    else {
      const key = message?.id;
      if (requests.has(key)) throw new Error('Deterministic worker requests need unique IDs');
      deterministicJob(new Promise<void>(resolve => requests.set(key, resolve)));
    }
    try { send(message, transfer ?? []); }
    catch (error) { requests.get(message?.id)?.(); requests.delete(message?.id); throw error; }
  }) as Worker['postMessage'];
  worker.addEventListener('message', event => { requests.get(event.data?.id)?.(); requests.delete(event.data?.id); });
  const release = (): void => { for (const finish of requests.values()) finish(); requests.clear(); };
  worker.addEventListener('error', release);
  worker.addEventListener('messageerror', release);
  const terminate = worker.terminate.bind(worker);
  worker.terminate = () => { release(); terminate(); };
  return worker;
}
